import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Search, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldAlert, 
  ShieldCheck, 
  FileText, 
  X, 
  Loader2, 
  Copy, 
  Check, 
  Scale, 
  ExternalLink 
} from 'lucide-react';

interface CounterpartyCheckerModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCode?: string;
  onSelectDossier?: (dossierText: string) => void;
  isUk?: boolean;
}

export default function CounterpartyCheckerModal({
  isOpen,
  onClose,
  initialCode = '',
  onSelectDossier,
  isUk = true
}: CounterpartyCheckerModalProps) {
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [dossier, setDossier] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (initialCode) {
      setCode(initialCode.trim().replace(/\D/g, ''));
    }
  }, [initialCode]);

  if (!isOpen) return null;

  // Real-time checksum evaluation
  const clean = code.trim().replace(/\D/g, '');
  const is8 = clean.length === 8;
  const is10 = clean.length === 10;
  const isFormatValid = is8 || is10;

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!clean) return;

    setLoading(true);
    setError(null);
    setDossier(null);

    try {
      const res = await fetch(`/api/registry/check-counterparty?code=${clean}`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || (isUk ? 'Помилка пошуку контрагента' : 'Search failed'));
      }
      const data = await res.json();
      if (!data.dossier) {
        throw new Error(isUk ? 'Контрагента не знайдено' : 'Counterparty not found');
      }
      setDossier(data.dossier);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Помилка зв\'язку з реєстрами' : 'Error connecting to registry'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyDossier = () => {
    if (!dossier) return;
    const text = `ДОСЬЄ КОНТРАГЕНТА (${dossier.code}):
Назва: ${dossier.name}
Статус: ${dossier.statusDescription}
Керівник: ${dossier.director}
Адреса: ${dossier.address}
КВЕД: ${dossier.kvedCode} — ${dossier.kvedName}
ПДВ: ${dossier.isVatPayer ? `Платник (ІПН ${dossier.vatNumber || dossier.code})` : 'Не платник'}
Податковий борг: ${dossier.hasTaxDebt ? `Наявний (${dossier.taxDebtAmountUah} грн)` : 'Відсутній'}
Судові справи (ЄДРСР): ${dossier.courtCasesTotal}
Ризиковість СМКОР (ПКМУ № 1165): ${dossier.smkorRiskScore}% (${dossier.smkorRiskLevel})`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    if (onSelectDossier) {
      onSelectDossier(text);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs font-mono animate-fade-in">
      <div className="relative w-full max-w-2xl bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 border-b border-slate-800 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg border border-emerald-500/30">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-wide">
                {isUk ? 'Реєстровий Аудит Контрагента (YouControl / ЄДР / ДПС)' : 'Counterparty Registry Audit (YouControl / EDR / DPS)'}
              </h3>
              <p className="text-[10px] text-slate-400">
                {isUk ? 'Перевірка ЄДРПОУ, банкрутства, судових справ ЄДРСР та ризиків СМКОР' : 'Verify EDRPOU, bankruptcy, court cases and SMKOR risks'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-6 bg-slate-50 border-b border-slate-200">
          <form onSubmit={handleSearch} className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={isUk ? 'Введіть код ЄДРПОУ (8 цифр) або РНОКПП (10 цифр)...' : 'Enter 8-digit EDRPOU or 10-digit RNOKPP...'}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-mono text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !clean}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-extrabold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 shadow-xs"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isUk ? 'Пошук...' : 'Auditing...'}</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>{isUk ? 'Перевірити' : 'Audit'}</span>
                </>
              )}
            </button>
          </form>

          {/* Quick Format Feedback */}
          {code && (
            <div className="mt-2.5 flex items-center gap-2 text-[11px]">
              <span className="text-slate-500">{isUk ? 'Формат коду:' : 'Format:'}</span>
              {is8 ? (
                <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded font-bold">
                  ✓ {isUk ? 'ЄДРПОУ (Юридична особа, 8 цифр)' : 'EDRPOU (Company, 8 digits)'}
                </span>
              ) : is10 ? (
                <span className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded font-bold">
                  ✓ {isUk ? 'РНОКПП (ФОП / Фізична особа, 10 цифр)' : 'RNOKPP (Individual/FOP, 10 digits)'}
                </span>
              ) : (
                <span className="text-amber-700 bg-amber-100 px-2 py-0.5 rounded font-bold">
                  ⚠️ {isUk ? 'Неповний код (має бути 8 або 10 цифр)' : 'Incomplete code'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {dossier && (
            <div className="space-y-4">
              {/* Header Card */}
              <div className="p-4 bg-slate-900 text-white rounded-xl space-y-2 border border-slate-800">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">
                      {dossier.legalForm || 'Компанія'} • {dossier.codeType}: {dossier.code}
                    </span>
                    <h4 className="text-sm font-bold text-slate-100 leading-snug">
                      {dossier.name}
                    </h4>
                  </div>
                  <span className={`px-2.5 py-1 text-[10px] font-extrabold uppercase rounded-md border shrink-0 ${
                    dossier.status === 'active' 
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' 
                      : 'bg-red-500/20 text-red-300 border-red-500/40'
                  }`}>
                    {dossier.statusDescription}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-800 text-slate-300">
                  <div>
                    <span className="text-slate-500">{isUk ? 'Керівник:' : 'Director:'} </span>
                    <span className="font-bold text-white">{dossier.director || 'Не вказано'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">{isUk ? 'Адреса:' : 'Address:'} </span>
                    <span className="truncate">{dossier.address || 'Україна'}</span>
                  </div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="text-[10px] text-slate-500 uppercase font-bold">{isUk ? 'Статус ПДВ' : 'VAT Status'}</span>
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    {dossier.isVatPayer ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400 shrink-0" />
                    )}
                    <span>{dossier.isVatPayer ? (isUk ? 'Платник ПДВ' : 'VAT Payer') : (isUk ? 'Не платник' : 'Non-payer')}</span>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="text-[10px] text-slate-500 uppercase font-bold">{isUk ? 'Податковий борг' : 'Tax Debt'}</span>
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    {dossier.hasTaxDebt ? (
                      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                    )}
                    <span>{dossier.hasTaxDebt ? `${dossier.taxDebtAmountUah} грн` : (isUk ? 'Відсутній' : 'None')}</span>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="text-[10px] text-slate-500 uppercase font-bold">{isUk ? 'Суди (ЄДРСР)' : 'Court Cases'}</span>
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <Scale className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>{dossier.courtCasesTotal} {isUk ? 'справ' : 'cases'}</span>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="text-[10px] text-slate-500 uppercase font-bold">{isUk ? 'Ризик СМКОР' : 'SMKOR Risk'}</span>
                  <div className={`text-xs font-extrabold flex items-center gap-1.5 ${
                    dossier.smkorRiskLevel === 'LOW' 
                      ? 'text-emerald-700' 
                      : dossier.smkorRiskLevel === 'MEDIUM' 
                        ? 'text-amber-700' 
                        : 'text-red-700'
                  }`}>
                    {dossier.smkorRiskLevel === 'LOW' ? (
                      <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <ShieldAlert className="w-4 h-4 text-red-600 shrink-0" />
                    )}
                    <span>{dossier.smkorRiskScore}% ({dossier.smkorRiskLevel})</span>
                  </div>
                </div>
              </div>

              {/* Legal Notes */}
              <div className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-xl text-xs space-y-1.5 text-emerald-950">
                <span className="font-bold text-[10px] uppercase text-emerald-800 tracking-wider flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span>{isUk ? 'Висновки автоматичного юридичного комплаєнсу' : 'Automated Compliance Summary'}</span>
                </span>
                <p className="leading-relaxed">
                  {dossier.legalRecommendations && dossier.legalRecommendations.length > 0
                    ? dossier.legalRecommendations.join(' ')
                    : (isUk ? 'Контрагент має діючий реєстраційний статус. Ризиків банкрутства чи анулювання ПДВ не виявлено.' : 'Active registration status verified.')}
                </p>
                <div className="text-[10px] text-emerald-700 pt-1">
                  {isUk ? 'Джерело даних:' : 'Data source:'} {dossier.dataSource} • {new Date(dossier.auditTimestamp).toLocaleDateString('uk-UA')}
                </div>
              </div>
            </div>
          )}

          {!dossier && !loading && !error && (
            <div className="py-12 text-center text-slate-400 space-y-2">
              <Building2 className="w-10 h-10 mx-auto text-slate-300" />
              <p className="text-xs">
                {isUk ? 'Введіть код ЄДРПОУ або РНОКПП для миттєвої перевірки правоздатності' : 'Enter company code to verify registration and legal status'}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-slate-100 border-t border-slate-200 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold uppercase rounded-lg transition-colors cursor-pointer"
          >
            {isUk ? 'Закрити' : 'Close'}
          </button>

          {dossier && (
            <button
              onClick={handleCopyDossier}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer shadow-xs"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span>{copied ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Скопіювати досьє' : 'Copy Dossier')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
